import { useQueries, useQuery } from '@tanstack/react-query'
import { isDefined } from '@towns-protocol/sdk'
import { useConnectivity, useLinkedAndDelegatedWallets } from 'use-towns-client'
import { env } from 'utils'
import { createPublicClient, http } from 'viem'
import { mainnet } from 'viem/chains'

const ethMainnetRpcUrl = env.VITE_ETHEREUM_RPC_URL
const publicClient = createPublicClient({
    chain: mainnet,
    transport: ethMainnetRpcUrl ? http(ethMainnetRpcUrl) : http(),
})

export function useEnsNames() {
    const { loggedInWalletAddress } = useConnectivity()

    const { data: linkedWallets, isLoading: isLoadingWallets } = useLinkedAndDelegatedWallets({
        walletAddress: loggedInWalletAddress,
    })

    const queries = (linkedWallets ?? []).map((wallet) => {
        return {
            queryKey: ['linkedWalletsEnsNames', wallet],
            queryFn: async () => {
                const ensName = await publicClient.getEnsName({ address: wallet as `0x${string}` })
                return { name: ensName as string | undefined, address: wallet }
            },
            staleTime: 1000 * 3600,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        }
    })

    const ensResponses = useQueries({ queries: queries })
    const ensNames = ensResponses
        .map((r) => r.data)
        .filter(isDefined)
        .filter((ens) => ens.name && ens.name.length > 0)

    const isLoadingEnsNames = ensResponses.some((response) => response.isLoading)
    return { ensNames: ensNames, isFetching: isLoadingWallets || isLoadingEnsNames }
}

export function useResolveEnsName({
    userId,
    ensAddress,
}: {
    userId: string | undefined
    ensAddress: string | undefined
}) {
    const { data: linkedWallets } = useLinkedAndDelegatedWallets({ walletAddress: userId })

    const linkedEnsWallet = linkedWallets?.find((x) => x === ensAddress)
    const { data: ensName, isLoading } = useQuery({
        queryKey: ['ensName', linkedEnsWallet],
        queryFn: () => publicClient.getEnsName({ address: linkedEnsWallet as `0x${string}` }),
        enabled: !!linkedEnsWallet,
    })

    return { resolvedEnsName: ensName, isFetching: isLoading }
}
